import { components, paths } from "@/api-schema";
import {
  PathsV1ApplicationSearchPostParametersQueryOrderByDirection,
  PathsV1ApplicationSearchPostParametersQueryOrderByField,
} from "@/api-schema-enums";
import { DEFAULT_PAGE_SIZE } from "@/common/api/_defaults";
import { useApplicationDeleteMutation } from "@/common/api/application/use-application-delete.mutation";
import { useApplicationSearchQuery } from "@/common/api/application/use-application-search.query";
import { RowWithIdentifier } from "@/components/data-table/_helpers/row-identifier";
import { DataTableHeightMode } from "@/components/data-table/types";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { createBasePredefinedTable } from "./_components/base-predefined-table";
import {
  BasePredefinedTableProps,
  DataFetchHooks,
  PredefinedTableConfig,
} from "./_components/base-predefined-table.types";
import { applicationTableDefaultColumns } from "./application-table-columns";

type Application = components["schemas"]["Application"] & RowWithIdentifier;
type ApplicationSorting = NonNullable<paths["/v1/application/search"]["post"]["parameters"]["query"]["orderBy"]>;
type ApplicationQueryParams = paths["/v1/application/search"]["post"]["parameters"]["query"];
type ApplicationRequestBody = paths["/v1/application/search"]["post"]["requestBody"]["content"]["application/json"];

const DEFAULT_COLUMNS = [
  applicationTableDefaultColumns.columnImage,
  applicationTableDefaultColumns.columnName,
  applicationTableDefaultColumns.columnIsV1Application,
];

const APPLICATION_TABLE_CONFIG: PredefinedTableConfig<ApplicationSorting> = {
  defaultPageSize: DEFAULT_PAGE_SIZE,
  defaultSorting: [
    {
      direction: PathsV1ApplicationSearchPostParametersQueryOrderByDirection.Asc,
      field: PathsV1ApplicationSearchPostParametersQueryOrderByField.Name,
    },
  ] as const,
  defaultTableConfig: {
    searchPlaceholder: i18next.t("common:SearchByAppName"),
    features: {
      multiSelection: false,
      pagination: true,
      search: true,
      expansion: false,
    },
  } as const,
  defaultToastMessages: {
    deleteSuccess: i18next.t("ram:AppHasBeenDeleted"),
    deleteError: i18next.t("common:ErrorDeletingApp"),
  } as const,
} as const;

const APPLICATION_TABLE_QUERY_HOOKS: DataFetchHooks<Application, ApplicationQueryParams, ApplicationRequestBody> = {
  useSearchQuery: useApplicationSearchQuery,
  useDeleteMutation: useApplicationDeleteMutation,
} as const;

export interface ApplicationTableProps extends BasePredefinedTableProps<
  Application,
  ApplicationSorting,
  ApplicationQueryParams,
  ApplicationRequestBody
> {}

const BaseApplicationTable = createBasePredefinedTable({
  config: APPLICATION_TABLE_CONFIG,
  queryHooks: APPLICATION_TABLE_QUERY_HOOKS,
  defaultColumns: DEFAULT_COLUMNS,
});

export function ApplicationTable(props: ApplicationTableProps) {
  const { t } = useTranslation(["common", "ram"]);
  const {
    name = "application-table",
    showCreateButton = true,
    heightMode = DataTableHeightMode.Viewport,
    ...restProps
  } = props;

  return (
    <BaseApplicationTable
      searchPlaceholder={t("common:SearchByAppName")}
      name={name}
      showCreateButton={showCreateButton}
      heightMode={heightMode}
      {...restProps}
    />
  );
}
